import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { Card, Container } from "react-bootstrap";
import AlertMessage from "../Common/AlertMessage";

import extractApiErrors from "../../utils/extractApiErrors";
import {
  createPermission,
  getPermissionById,
  updatePermission,
  getUniqueActions,
  getUniqueModules,
} from "../../services/securityPermissionService";

const SecurityPermissionForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [successMessage, setSuccessMessage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [availableActions, setAvailableActions] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);

  const validationSchema = Yup.object().shape({
    module: Yup.string()
      .trim()
      .required("El nombre del módulo es obligatorio")
      .matches(/^\S+$/, "No puede contener espacios"),
    action: Yup.string()
      .trim()
      .required("La acción es obligatoria")
      .matches(/^\S+$/, "No puede contener espacios"),
    description: Yup.string()
      .required("La descripción es obligatoria")
      .max(500, "La descripción no puede exceder los 500 caracteres"),
  });

  const formik = useFormik({
    initialValues: {
      module: "",
      action: "",
      description: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      setErrors([]);
      setSuccessMessage(null);

      try {
        const payload = { ...values };
        const response = id
          ? await updatePermission({ id, ...payload })
          : await createPermission(payload);

        if (!response?.success) throw { backend: response };

        setSuccessMessage(
          id
            ? "✅ Permiso actualizado correctamente."
            : "✅ Permiso creado correctamente."
        );
      } catch (error) {
        const parsedErrors = extractApiErrors(error);
        setErrors(parsedErrors);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const fetchInit = async () => {
      try {
        if (id) {
          const response = await getPermissionById(id);
          if (response.success) {
            const { module, action, description } = response.data;
            formik.setValues({ module, action, description });
          }
        }

        const [actionsRes, modulesRes] = await Promise.all([
          getUniqueActions(),
          getUniqueModules(),
        ]);

        if (actionsRes.success) setAvailableActions(actionsRes.data);
        if (modulesRes.success) setAvailableModules(modulesRes.data);
      } catch {
        Swal.fire("Error", "No se pudo cargar el formulario", "error");
      } finally {
      }
    };
    fetchInit();
  }, [id]);

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => {
        navigate("/dashboard/security-permission");
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage, navigate]);

  const generatedKey = `${formik.values.module}.${formik.values.action}`;

  return (
    <Container className="mt-4">
      <Card className="p-4 novian-form">
        <Card.Header>
          <Card.Title as="h3" className="text-dark">
            {id ? "Editar permiso de seguridad" : "Crear nuevo permiso"}
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <form onSubmit={formik.handleSubmit}>
            {/* Módulo (autocompletado) */}
            <div className="mb-3">
              <label className="form-label">Módulo</label>
              <input
                list="module-options"
                name="module"
                className="form-control"
                value={formik.values.module}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Ej: dashboard"
              />
              <datalist id="module-options">
                {availableModules.map((module, index) => (
                  <option key={index} value={module} />
                ))}
              </datalist>
              {formik.touched.module && formik.errors.module && (
                <div className="text-danger small mt-1">
                  {formik.errors.module}
                </div>
              )}
            </div>

            {/* Acción (autocompletado) */}
            <div className="mb-3">
              <label className="form-label">Acción</label>
              <input
                list="action-options"
                name="action"
                className="form-control"
                value={formik.values.action}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Ej: view, create, delete..."
              />
              <datalist id="action-options">
                {availableActions.map((action, index) => (
                  <option key={index} value={action} />
                ))}
              </datalist>
              {formik.touched.action && formik.errors.action && (
                <div className="text-danger small mt-1">
                  {formik.errors.action}
                </div>
              )}
            </div>

            {/* Descripción */}
            <div className="mb-3">
              <label className="form-label">Descripción</label>
              <textarea
                name="description"
                className="form-control"
                rows={3}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Ej: Ver el dashboard de la empresa"
              />
              {formik.touched.description && formik.errors.description && (
                <div className="text-danger small mt-1">
                  {formik.errors.description}
                </div>
              )}
            </div>

            {/* Clave generada */}
            <div className="mb-4">
              <strong className="text-dark">Clave generada:</strong>
              <span className="badge bg-secondary ms-2">{generatedKey}</span>
            </div>

            {/* Alertas */}
            {errors.length > 0 && (
              <AlertMessage
                type="error"
                message={
                  <ul className="mb-0">
                    {errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                }
                onClose={() => setErrors([])}
              />
            )}

            {successMessage && (
              <AlertMessage
                type="success"
                message={successMessage}
                onClose={() => setSuccessMessage(null)}
              />
            )}

            {/* Botones */}
            <div className="d-flex justify-content-between">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate("/dashboard/security-permission")}
              >
                Volver
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting
                  ? "Guardando..."
                  : id
                  ? "Actualizar"
                  : "Crear Permiso"}
              </button>
            </div>
          </form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SecurityPermissionForm;
